import { styleConstants } from '@paywithextend/components'
import { useQueryClient } from '@tanstack/react-query'
import Button from 'ext-ui/components/Button/Button'
import { spacing } from 'ext-ui/style/spacing'
import ApiKeyUpsellSvg from 'ext-ui/svg/illustration/ApiKeyUpsellSvg'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import APIKeysTable from 'src/app/APIProduct/APIKeysTable'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import Box from 'src/components/Box/Box'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import SaasUpgradeOrContent from 'src/feature/SaasUpgradeOrContent/SaasUpgradeOrContent'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import Lnk from 'src/navigation/Lnk'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  APIUserStatus,
  usePostAPIKey,
  usePostAPIUser,
  useQueryAPIKeys,
  useQueryAPIUser,
} from 'src/queries/organizations/apikeys.queries'
import { apiUsersQueryKeys } from 'src/queries/queries.constants'
import {
  selectAppName,
  selectBrandFeaturesFromState,
} from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const APIKeysInfo: React.FC = () => {
  const queryClient = useQueryClient()
  const user = useCurrentUser()
  const appName = useSelector(selectAppName)
  const { analytics } = useAnalyticsContext()
  const {
    data: apiUser,
    isFetching: apiUserLoading,
    isPending: apiUserPending,
  } = useQueryAPIUser({})
  const { mutate: postCreateApiUser, isPending: postCreateApiUserLoading } =
    usePostAPIUser()
  const {
    data: apiKeys,
    isFetching: apiKeysLoading,
    isPending: apiKeysPending,
  } = useQueryAPIKeys({})
  const { mutate: postCreateAPIKey, isPending: postCreateAPIKeyLoading } =
    usePostAPIKey()
  const emptyAPIKeys = apiKeys === undefined || apiKeys.apiKeys.length === 0
  const showEmptyUserState = apiUser === undefined
  const showPendingState =
    apiUser !== undefined && apiUser.status === APIUserStatus.PENDING_APPROVAL
  const showDeniedState =
    apiUser !== undefined && apiUser.status === APIUserStatus.DENIED

  const showEmptyAPIKeysState =
    emptyAPIKeys && !showEmptyUserState && !showPendingState && !showDeniedState
  const { API_KEY_MANAGER: apiKeyManagerEnabled } =
    useQueryUserFeatures([UserFeature.API_KEY_MANAGER]).data || {}
  const { apiKeysEnabled } = useSelector(selectBrandFeaturesFromState)

  const translate = useTranslate()

  const EmptyStateContent: React.FC = (): React.ReactElement => {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        `}
      >
        <TableEmptyState
          design={
            showPendingState
              ? TableEmptyStateDesigns.APIProductPending
              : showEmptyAPIKeysState
                ? TableEmptyStateDesigns.APIProductEmptyAPIKeys
                : showDeniedState
                  ? TableEmptyStateDesigns.APIProductDenied
                  : TableEmptyStateDesigns.APIProduct
          }
          wide
          buttonClickAction={(): void => {
            if (showEmptyAPIKeysState) {
              analytics.track('apiKey - request api key')
              postCreateAPIKey(
                { body: {} },
                {
                  onSuccess: () =>
                    queryClient.invalidateQueries({
                      queryKey: apiUsersQueryKeys.me(),
                    }),
                },
              )
            } else if (showEmptyUserState) {
              analytics.track('apiKey - request api user')
              postCreateApiUser(
                { body: {} },
                {
                  onSuccess: () =>
                    queryClient.invalidateQueries({
                      queryKey: apiUsersQueryKeys.me(),
                    }),
                },
              )
            }
          }}
        />
      </div>
    )
  }

  const APIKeyTableContent: React.FC = (): React.ReactElement => {
    return (
      <Box
        noPadding
        noShadow
        outlineBorder
        css={css`
          overflow: hidden;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            padding: ${spacing(7)} ${spacing(6)};
            flex-grow: 2;
            border-top-left-radius: 10px;
            border-top-right-radius: 10px;
            background-color: ${styleConstants.colors.white};
            border-right: 1px solid ${styleConstants.colors.white};
            border-bottom: 1px solid ${styleConstants.colors.white};
            transition:
              background-color 150ms ease-out,
              border 150ms ease-out,
              margin 150ms ease-out,
              padding 150ms ease-out;
          `}
        >
          <div
            css={css`
              display: flex;
              width: 100%;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <div>
              <div>
                <div className={'font-support-medium'}>
                  {translate('Only 2 keys allowed at one time.')}
                </div>
              </div>
            </div>
          </div>
        </div>
        <APIKeysTable apiKeys={apiKeys?.apiKeys ?? []} />
      </Box>
    )
  }

  const showLoadingState =
    user === undefined ||
    apiUserLoading ||
    apiUserPending ||
    postCreateApiUserLoading ||
    apiKeysLoading ||
    apiKeysPending ||
    postCreateAPIKeyLoading

  if (showLoadingState) {
    return <LoadingBackgroundPage />
  }

  if (!apiKeysEnabled || !apiKeyManagerEnabled) {
    return <NotFoundBackgroundPageContent />
  }

  const showEmptyState =
    showEmptyUserState ||
    showPendingState ||
    showEmptyAPIKeysState ||
    showDeniedState

  return (
    <>
      <Helmet>
        <title>{`API Keys | ${appName}`}</title>
        <meta name="description" content={`API Keys page of ${appName}`} />
      </Helmet>
      <LayoutPage padding={'none'}>
        <SaasUpgradeOrContent
          illustration={<ApiKeyUpsellSvg />}
          title={translate('API Key')}
          description={translate(
            'Generate your API key and unlock the tools to embed virtual card* issuing, approvals, transaction monitoring, and expense processing capabilities into your own workflows.',
          )}
          disclaimer={translate(
            '*Virtual cards available for ${selectIssuersLink}',
            {
              selectIssuersLink: (
                <Lnk
                  className="underline-link"
                  href="https://support.paywithextend.com/hc/en-us/sections/35632429037079-Registering-a-Card-Account"
                  ariaLabel={translate('select issuers')}
                >
                  {translate('select issuers')}
                </Lnk>
              ),
            },
          )}
          ctaDetailSrc={
            'Upgrade to ${tier} today and start customizing your own payment experiences.'
          }
          feature={UserFeature.API_KEY}
        >
          <LayoutPage
            addRightShift={false}
            title={<span>{translate('API Key')}</span>}
            actions={
              <Button
                disabled={
                  postCreateAPIKeyLoading || apiKeys?.remainingApiKeyQuota === 0
                }
                variant={'primary'}
                dropShadow={false}
                onClick={(): void => {
                  analytics.track('apiKeys - request api key')
                  postCreateAPIKey({ body: {} })
                }}
              >
                {translate('Generate New Key +')}
              </Button>
            }
          >
            {showEmptyState ? <EmptyStateContent /> : <APIKeyTableContent />}
          </LayoutPage>
        </SaasUpgradeOrContent>
      </LayoutPage>
    </>
  )
}

export default APIKeysInfo
